import { ProductThumbnail, ProductThumbnailValue } from '../../type';
import { IMAGE_ASSETS } from '@services/MarppleShop/shared/util/image-asset.ts';
import { isNull, isObject, isString } from '@fxts/core';
import { ScaleVO } from '@services/MarppleShop/shared/value-objects/scale.ts';
import { ProductRow } from '../types';

export interface Dimensions {
  width: number;
  max_size: number;
  padding_size: number;
}

export const thumbnailTransformer = {
  /**
   * 성인 인증 썸네일 생성
   */
  createAdultThumbnail: (locale: 'kr' | 'en' | 'jp'): ProductThumbnail => ({
    imageUrl: locale === 'kr' ? IMAGE_ASSETS.AGE_19_THUMB : IMAGE_ASSETS.AGE_18_THUMB,
    scale: ScaleVO.create(1).value,
    dimensions: {},
  }),

  /**
   * 썸네일 데이터에서 메인 썸네일 추출
   */
  extractMainThumbnail: (thumbnails: ProductRow['thumbnails']): ProductThumbnailValue => {
    const defaults = {
      is_thumb: true,
      url: IMAGE_ASSETS.ERROR_THUMB,
    } as const;

    if (!isObject(thumbnails) || !('value' in thumbnails)) {
      return defaults;
    }

    try {
      const thumbnail = Array.isArray(thumbnails.value)
        ? (thumbnails.value.find((thumb: unknown) => {
            return thumb && isObject(thumb) && 'is_thumb' in thumb && thumb.is_thumb === true;
          }) as ProductThumbnailValue)
        : null;

      return thumbnail ?? defaults;
    } catch (e) {
      return defaults;
    }
  },

  /**
   * 썸네일 스케일 계산 (composite_template_id 고려)
   */
  calculateScale: (thumbnail: ProductThumbnailValue, thumbnail_ratio: number | null): ScaleVO => {
    const defaults = ScaleVO.create(0.84);

    if (thumbnail.composite_template_id) {
      return ScaleVO.create(1);
    }

    try {
      if (isString(thumbnail_ratio)) {
        console.warn('thumbnailRatioPercent must be a number');
        return ScaleVO.create(parseFloat(thumbnail_ratio));
      }
      if (isNull(thumbnail_ratio)) {
        return defaults;
      }
      return ScaleVO.create(thumbnail_ratio);
    } catch (_e) {
      return defaults;
    }
  },

  /**
   * 썸네일 처리 함수
   */
  transform: (
    data: Pick<ProductRow, 'adult_check_needed' | 'thumbnails' | 'thumbnail_ratio'>,
  ): ProductThumbnail => {
    // 1. 썸네일 추출
    const thumbnail = thumbnailTransformer.extractMainThumbnail(data.thumbnails);

    // 3. 스케일 계산
    const scaleVO = thumbnailTransformer.calculateScale(thumbnail, data.thumbnail_ratio);

    // 썸네일에 있던 로직 옮겨오기
    const resizeLevel =
      thumbnail.is_pb &&
      thumbnail.width &&
      thumbnail.height &&
      thumbnail.width !== thumbnail.height &&
      // thumbnail을 만들면서 이미지가 정사각형이어도 1px정도가 틀어지는 예외 케이스가 발생했습니다.
      Math.abs(thumbnail.width - thumbnail.height) > 1
        ? 80
        : undefined;

    return {
      composite_template_id: thumbnail.composite_template_id,
      imageUrl: thumbnail.url,
      scale: scaleVO.value,
      dimensions: {
        width: thumbnail?.width,
        height: thumbnail?.height,
        resizeLevel,
      },
    };
  },
};
